import React, { useState, useEffect, useCallback } from "react";
import { Container, Card, Table, Button, Modal, Form, Alert, Tabs, Tab, Badge, InputGroup } from "react-bootstrap";
import { FaPlus, FaEdit, FaTrash, FaExclamationTriangle, FaTags, FaBoxOpen, FaSlidersH, FaMinus } from "react-icons/fa";
import Swal from "sweetalert2";
import { 
  getAllInventoryApi, createInventoryApi, updateInventoryApi, deleteInventoryApi,
  getAllCategoriesApi, createCategoryApi, updateCategoryApi, deleteCategoryApi,
  adjustInventoryApi, getInventoryLotsApi
} from "./service/api_service";
import { useAuth } from "../authen/AuthContext";

export default function Inventory() {
  const { user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState("items");
  
  // States for Inventory Items
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(true);
  const [showItemModal, setShowItemModal] = useState(false);
  const [itemFormData, setItemFormData] = useState({ id: null, name: "", type: "direct_cost", category_id: "", unit: "", min_stock: 10 });

  // Filter & Search States
  const [searchQuery, setSearchQuery] = useState("");
  const [searchCatQuery, setSearchCatQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterType, setFilterType] = useState("");

  // States for Categories
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [showCatModal, setShowCatModal] = useState(false);
  const [catFormData, setCatFormData] = useState({ id: null, name: "" });

  // Common states
  const [error, setError] = useState("");
  const [modalMode, setModalMode] = useState("add"); 
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ type: "", id: null, name: "" });

  // Adjust Stock Modal State
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustData, setAdjustData] = useState({ id: null, name: "", adjust_quantity: "", reason: "", type: "" });

  // View Lots Modal State
  const [showLotsModal, setShowLotsModal] = useState(false);
  const [lotsData, setLotsData] = useState([]);
  const [loadingLots, setLoadingLots] = useState(false);
  const [selectedItemName, setSelectedItemName] = useState("");

  const token = localStorage.getItem("token");
  const canEdit = currentUser?.role === "master" || currentUser?.role === "admin" || currentUser?.role === "kitchen";

  const fetchItems = useCallback(async () => {
    try {
      setLoadingItems(true);
      const res = await getAllInventoryApi(token);
      setItems(res.data);
    } catch (err) {
      setError("Failed to fetch inventory items");
    } finally {
      setLoadingItems(false);
    }
  }, [token]);

  const fetchCategories = useCallback(async () => {
    try {
      setLoadingCategories(true);
      const res = await getAllCategoriesApi(token);
      setCategories(res.data);
    } catch (err) {
      setError("Failed to fetch categories");
    } finally {
      setLoadingCategories(false);
    }
  }, [token]);

  useEffect(() => {
    fetchItems();
    fetchCategories();
  }, [fetchItems, fetchCategories]);

  // Derived filtered items
  const filteredItems = items.filter(item => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = item.name.toLowerCase().includes(searchLower) || 
                          (item.category_name && item.category_name.toLowerCase().includes(searchLower));
    const matchesCategory = filterCategory ? String(item.category_id) === String(filterCategory) : true;
    const matchesType = filterType ? item.type === filterType : true;
    
    return matchesSearch && matchesCategory && matchesType;
  });

  // Derived filtered categories
  const filteredCategories = categories.filter(cat => 
    cat.name.toLowerCase().includes(searchCatQuery.toLowerCase())
  );

  // --- Item Handlers ---
  const handleShowAddItem = () => {
    setModalMode("add");
    setItemFormData({ id: null, name: "", type: "direct_cost", category_id: categories.length ? categories[0].id : "", unit: "", min_stock: 10 });
    setShowItemModal(true);
    setError("");
  };

  const handleShowEditItem = (item) => {
    setModalMode("edit");
    setItemFormData({ id: item.id, name: item.name, type: item.type, category_id: item.category_id || "", unit: item.unit, min_stock: item.min_stock !== null ? item.min_stock : 10 });
    setShowItemModal(true);
    setError("");
  };

  const handleItemSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = { ...itemFormData, category_id: itemFormData.category_id ? Number(itemFormData.category_id) : null };
      if (modalMode === "add") await createInventoryApi(token, payload);
      else await updateInventoryApi(token, payload.id, payload);
      setShowItemModal(false);
      fetchItems();
    } catch (err) {
      setError(err.response?.data?.message || "An error occurred");
    }
  };

  // --- Category Handlers ---
  const handleShowAddCat = () => {
    setModalMode("add");
    setCatFormData({ id: null, name: "" });
    setShowCatModal(true);
    setError("");
  };

  const handleShowEditCat = (cat) => {
    setModalMode("edit");
    setCatFormData({ id: cat.id, name: cat.name });
    setShowCatModal(true);
    setError("");
  };

  const handleCatSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (modalMode === "add") await createCategoryApi(token, catFormData);
      else await updateCategoryApi(token, catFormData.id, catFormData);
      setShowCatModal(false);
      fetchCategories();
    } catch (err) {
      setError(err.response?.data?.message || "An error occurred");
    }
  };

  // --- Delete Handlers ---
  const confirmDelete = (type, id, name) => {
    setDeleteTarget({ type, id, name });
    setShowConfirmModal(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget.id) return;
    try {
      if (deleteTarget.type === "item") {
        await deleteInventoryApi(token, deleteTarget.id);
        fetchItems();
      } else {
        await deleteCategoryApi(token, deleteTarget.id);
        fetchCategories();
      }
      setShowConfirmModal(false);
      setDeleteTarget({ type: "", id: null, name: "" });
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete");
      setShowConfirmModal(false);
    }
  };

  // --- Adjust Handlers ---
  const handleShowAdjust = (item) => {
    setAdjustData({ id: item.id, name: item.name, adjust_quantity: "", reason: "", type: "" });
    setShowAdjustModal(true);
    setError("");
  };

  const handleAdjustSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!adjustData.type) {
      setError("Please select whether to Add (+) or Deduct (-)");
      return;
    }

    if (!adjustData.adjust_quantity || Number(adjustData.adjust_quantity) <= 0) {
      setError("Please enter a valid amount greater than 0");
      return;
    }

    const isDeduct = adjustData.type === 'deduct';
    const finalAmount = isDeduct ? -Math.abs(Number(adjustData.adjust_quantity)) : Math.abs(Number(adjustData.adjust_quantity));
    const typeLabel = isDeduct ? "Deduct (-)" : "Add (+)";
    const confirmColor = isDeduct ? "#dc3545" : "#198754";

    const result = await Swal.fire({
      title: "Confirm Adjustment?",
      html: `You are about to <strong>${typeLabel} ${Math.abs(finalAmount)}</strong> units of <strong>${adjustData.name}</strong>.<br/><br/>Reason: ${adjustData.reason}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: confirmColor,
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Confirm"
    });

    if (!result.isConfirmed) return;

    try {
      await adjustInventoryApi(token, adjustData.id, {
        adjust_quantity: finalAmount,
        reason: adjustData.reason
      });
      setShowAdjustModal(false);
      Swal.fire("Success", "Stock adjusted successfully", "success");
      fetchItems();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to adjust stock");
    }
  };

  const handleShowLots = async (item) => {
    setSelectedItemName(item.name);
    setShowLotsModal(true);
    setLoadingLots(true);
    try {
      const res = await getInventoryLotsApi(token, item.id);
      setLotsData(res.data);
    } catch (err) {
      setError("Failed to fetch lots");
    } finally {
      setLoadingLots(false);
    }
  };

  return (
    <Container fluid className="p-0" style={{ backgroundColor: "#f8f9fa", minHeight: "100%" }}>
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
        <div>
          <h2 className="fw-bold mb-0" style={{ color: "#1e3c72" }}><FaBoxOpen className="me-2" /> Inventory & Stock</h2>
          <p className="text-muted mb-0 mt-1">Manage items, stock levels, and categories</p>
        </div>
        
        <div className="d-flex flex-wrap align-items-center gap-2">
          {activeTab === "items" && (
            <>
              <Form.Control 
                type="text" 
                placeholder="Search Items..." 
                className="shadow-sm border-0 bg-white"
                style={{ width: '250px' }}
                value={searchQuery} 
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Form.Select 
                className="shadow-sm border-0 bg-white"
                style={{ width: '200px' }}
                value={filterCategory} 
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <option value="">All Categories</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Form.Select>
              <Form.Select 
                className="shadow-sm border-0 bg-white"
                style={{ width: '150px' }}
                value={filterType} 
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="">All Types</option>
                <option value="direct_cost">Direct Cost</option>
                <option value="sunk_cost">Sunk Cost</option>
              </Form.Select>
              {canEdit && <Button className="px-4 shadow-sm ms-2 rounded text-white" style={{ backgroundColor: "#1e3c72", borderColor: "#1e3c72" }} onClick={handleShowAddItem}><FaPlus className="me-2"/> Add Item</Button>}
            </>
          )}

          {activeTab === "categories" && (
            <>
              <Form.Control 
                type="text" 
                placeholder="Search Categories..." 
                className="shadow-sm border-0 bg-white"
                style={{ width: '250px' }}
                value={searchCatQuery} 
                onChange={(e) => setSearchCatQuery(e.target.value)}
              />
              {canEdit && <Button className="px-4 shadow-sm ms-2 rounded text-white" style={{ backgroundColor: "#1e3c72", borderColor: "#1e3c72" }} onClick={handleShowAddCat}><FaPlus className="me-2"/> Add Category</Button>}
            </>
          )}
        </div>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}

      <Card className="shadow-sm border-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="px-3 pt-3 border-bottom-0">
        <Tab eventKey="items" title={<span><FaBoxOpen className="me-2"/>Inventory Items</span>}>
          
              {/* Desktop View */}
              <Table responsive hover className="mb-0 align-middle d-none d-md-table bg-white">
                <thead className="bg-light text-muted">
                  <tr>
                    <th className="ps-4">ID</th>
                    <th>Name</th>
                    <th>Category</th>
                    <th>Type</th>
                    <th className="text-end">Min Stock</th>
                    <th className="text-end">Stock Quantity</th>
                    {canEdit && <th className="text-end pe-4">Actions</th>}
                  </tr>
                </thead>
                <tbody className="border-top-0">
                  {loadingItems ? (
                    <tr><td colSpan={canEdit ? 6 : 5} className="text-center py-4">Loading...</td></tr>
                  ) : filteredItems.length === 0 ? (
                    <tr><td colSpan={canEdit ? 6 : 5} className="text-center py-4">No inventory items found</td></tr>
                  ) : (
                    filteredItems.map((item) => (
                      <tr key={item.id}>
                        <td className="ps-4 fw-bold text-muted">#{item.id}</td>
                        <td className="fw-semibold">{item.name}</td>
                        <td>{item.category_name || <span className="text-muted fst-italic">None</span>}</td>
                        <td>
                          <Badge bg={item.type === 'direct_cost' ? 'info' : 'warning'} className="px-2 py-1 rounded-pill">
                            {item.type === 'direct_cost' ? 'Direct Cost' : 'Sunk Cost'}
                          </Badge>
                        </td>
                        <td className="text-end fw-semibold text-muted">
                          {Number(item.min_stock ?? 10).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {item.unit}
                        </td>
                        <td className="text-end fw-bold" style={{ color: Number(item.stock_quantity) <= Number(item.min_stock ?? 10) ? 'red' : 'green' }}>
                          {Number(item.stock_quantity).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {item.unit}
                        </td>
                        {canEdit && (
                          <td className="text-end pe-4 text-nowrap">
                            <Button variant="light" size="sm" className="me-2 text-primary" onClick={() => handleShowLots(item)} title="View Lots (FIFO)"><FaBoxOpen /></Button>
                            <Button variant="light" size="sm" className="me-2 text-info" onClick={() => handleShowAdjust(item)} title="Adjust Stock"><FaSlidersH /></Button>
                            <Button variant="light" size="sm" className="me-2 text-primary" onClick={() => handleShowEditItem(item)} title="Edit"><FaEdit /></Button>
                            {Number(item.stock_quantity) <= 0 && (
                              <Button variant="light" size="sm" className="text-danger" onClick={() => confirmDelete('item', item.id, item.name)} title="Delete"><FaTrash /></Button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
              
              {/* Mobile View */}
              <div className="d-block d-md-none p-3" style={{ background: "#f4f6f9" }}>
                {loadingItems ? (
                  <div className="text-center py-4">Loading...</div>
                ) : filteredItems.length === 0 ? (
                  <div className="text-center py-4">No inventory items found</div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {filteredItems.map(item => (
                      <Card key={item.id} className="border-0 shadow-sm rounded-3">
                        <Card.Body className="p-3">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <h6 className="fw-bold m-0 text-dark" style={{ fontSize: "1.1rem" }}>{item.name}</h6>
                            <Badge bg={item.type === 'direct_cost' ? 'info' : 'warning'} className="px-2 py-1 rounded-pill">
                              {item.type === 'direct_cost' ? 'Direct Cost' : 'Sunk Cost'}
                            </Badge>
                          </div>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <div className="text-muted small">
                              <FaTags className="me-1" /> {item.category_name || "Uncategorized"}
                            </div>
                            <div className="small fw-bold text-muted">#{item.id}</div>
                          </div>
                          
                          <div className="d-flex justify-content-between align-items-end pt-3 border-top">
                            <div className="d-flex gap-4">
                              <div>
                                <div className="small text-muted mb-1">Min</div>
                                <div className="fw-semibold text-muted" style={{ fontSize: "1.1rem" }}>
                                  {Number(item.min_stock ?? 10).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span className="small">{item.unit}</span>
                                </div>
                              </div>
                              <div>
                                <div className="small text-muted mb-1">Stock</div>
                                <div style={{ color: Number(item.stock_quantity) <= Number(item.min_stock ?? 10) ? 'red' : 'green', fontSize: "1.1rem" }} className="fw-bold">
                                  {Number(item.stock_quantity).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span className="small">{item.unit}</span>
                                </div>
                              </div>
                            </div>
                            {canEdit && (
                              <div className="d-flex gap-2">
                                <Button variant="light" size="sm" className="text-primary bg-opacity-10 border-0" onClick={() => handleShowLots(item)} title="View Lots"><FaBoxOpen /></Button>
                                <Button variant="light" size="sm" className="text-info bg-opacity-10 border-0" onClick={() => handleShowAdjust(item)} title="Adjust Stock"><FaSlidersH /></Button>
                                <Button variant="light" size="sm" className="text-primary bg-opacity-10 border-0" onClick={() => handleShowEditItem(item)} title="Edit"><FaEdit /></Button>
                                {Number(item.stock_quantity) <= 0 && (
                                  <Button variant="light" size="sm" className="text-danger bg-opacity-10 border-0" onClick={() => confirmDelete('item', item.id, item.name)} title="Delete"><FaTrash /></Button>
                                )}
                              </div>
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            
        </Tab>
        
        <Tab eventKey="categories" title={<span><FaTags className="me-2"/>Categories</span>}>
          
              {/* Desktop View */}
              <Table responsive hover className="mb-0 align-middle d-none d-md-table bg-white">
                <thead className="bg-light text-muted">
                  <tr>
                    <th className="ps-4">ID</th>
                    <th>Category Name</th>
                    {canEdit && <th className="text-end pe-4">Actions</th>}
                  </tr>
                </thead>
                <tbody className="border-top-0">
                  {loadingCategories ? (
                    <tr><td colSpan={canEdit ? 3 : 2} className="text-center py-4">Loading...</td></tr>
                  ) : filteredCategories.length === 0 ? (
                    <tr><td colSpan={canEdit ? 3 : 2} className="text-center py-4">No categories found</td></tr>
                  ) : (
                    filteredCategories.map((cat) => (
                      <tr key={cat.id}>
                        <td className="ps-4 fw-bold text-muted">#{cat.id}</td>
                        <td className="fw-semibold">{cat.name}</td>
                        {canEdit && (
                          <td className="text-end pe-4">
                            <Button variant="light" size="sm" className="me-2 text-primary" onClick={() => handleShowEditCat(cat)}><FaEdit /></Button>
                            <Button variant="light" size="sm" className="text-danger" onClick={() => confirmDelete('category', cat.id, cat.name)}><FaTrash /></Button>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>

              {/* Mobile View */}
              <div className="d-block d-md-none p-3" style={{ background: "#f4f6f9" }}>
                {loadingCategories ? (
                  <div className="text-center py-4">Loading...</div>
                ) : filteredCategories.length === 0 ? (
                  <div className="text-center py-4">No categories found</div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {filteredCategories.map((cat) => (
                      <Card key={cat.id} className="border-0 shadow-sm rounded-3">
                        <Card.Body className="p-3 d-flex justify-content-between align-items-center">
                          <div>
                            <div className="small fw-bold text-muted mb-1">#{cat.id}</div>
                            <h6 className="fw-bold m-0 text-dark">{cat.name}</h6>
                          </div>
                          {canEdit && (
                            <div className="d-flex gap-2">
                              <Button variant="light" size="sm" className="text-primary bg-opacity-10 border-0" onClick={() => handleShowEditCat(cat)}><FaEdit /></Button>
                              <Button variant="light" size="sm" className="text-danger bg-opacity-10 border-0" onClick={() => confirmDelete('category', cat.id, cat.name)}><FaTrash /></Button>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            
        </Tab>
      </Tabs>
      </Card>

      {/* Add/Edit Item Modal */}
      <Modal show={showItemModal} onHide={() => setShowItemModal(false)} centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title style={{ color: "#1e3c72", fontWeight: 700 }}>
            {modalMode === "add" ? "Add Inventory Item" : "Edit Inventory Item"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleItemSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold text-secondary">Item Name</Form.Label>
              <Form.Control type="text" required value={itemFormData.name} onChange={(e) => setItemFormData({...itemFormData, name: e.target.value})} placeholder="e.g. Milk" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold text-secondary">Category</Form.Label>
              <Form.Select value={itemFormData.category_id} onChange={(e) => setItemFormData({...itemFormData, category_id: e.target.value})}>
                <option value="">-- No Category --</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold text-secondary">Type</Form.Label>
              <Form.Select value={itemFormData.type} onChange={(e) => setItemFormData({...itemFormData, type: e.target.value})}>
                <option value="direct_cost">Direct Cost (e.g. Milk, Beans)</option>
                <option value="sunk_cost">Sunk Cost (e.g. Napkins, Cups)</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold text-secondary">Unit</Form.Label>
              <Form.Control type="text" required value={itemFormData.unit} onChange={(e) => setItemFormData({...itemFormData, unit: e.target.value})} placeholder="e.g. ml, g, pieces" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold text-secondary">Min Stock (Alert)</Form.Label>
              <Form.Control type="number" step="0.01" min="0" required value={itemFormData.min_stock} onChange={(e) => setItemFormData({...itemFormData, min_stock: e.target.value})} placeholder="e.g. 10" />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0">
            <Button variant="light" onClick={() => setShowItemModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit" style={{ background: "#1e3c72", border: "none" }}>{modalMode === "add" ? "Create Item" : "Save Changes"}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Add/Edit Category Modal */}
      <Modal show={showCatModal} onHide={() => setShowCatModal(false)} centered size="sm">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title style={{ color: "#1e3c72", fontWeight: 700 }}>
            {modalMode === "add" ? "Add Category" : "Edit Category"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCatSubmit}>
          <Modal.Body>
            <Form.Group>
              <Form.Label className="fw-semibold text-secondary">Category Name</Form.Label>
              <Form.Control type="text" required value={catFormData.name} onChange={(e) => setCatFormData({...catFormData, name: e.target.value})} placeholder="e.g. Dairy" />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0 mt-3">
            <Button variant="light" onClick={() => setShowCatModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit" style={{ background: "#1e3c72", border: "none" }}>{modalMode === "add" ? "Create" : "Save"}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered size="sm">
        <Modal.Body className="text-center p-4">
          <FaExclamationTriangle className="text-warning mb-3" size={48} />
          <h5 className="fw-bold mb-3">Delete {deleteTarget.type === 'item' ? 'Item' : 'Category'}</h5>
          <p className="text-muted mb-4">
            Are you sure you want to delete <strong>{deleteTarget.name}</strong>?
          </p>
          <div className="d-flex justify-content-center gap-2">
            <Button variant="light" onClick={() => setShowConfirmModal(false)} className="px-4">Cancel</Button>
            <Button variant="danger" onClick={handleDelete} className="px-4">Delete</Button>
          </div>
        </Modal.Body>
      </Modal>
      {/* Adjust Stock Modal */}
      <Modal show={showAdjustModal} onHide={() => setShowAdjustModal(false)} centered size="md">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title style={{ color: "#1e3c72", fontWeight: 700 }}>
            Adjust Stock Level
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAdjustSubmit}>
          <Modal.Body className="px-4">
            <div className="mb-4 text-center p-3 rounded" style={{ backgroundColor: "#f1f4f8" }}>
              <span className="text-muted small text-uppercase fw-bold">Target Item</span>
              <h4 className="fw-bold m-0 text-dark mt-1">{adjustData.name}</h4>
            </div>
            
            <Form.Group className="mb-4">
              <Form.Label className="fw-bold text-dark">Adjustment Amount</Form.Label>
              <InputGroup size="lg">
                <Button 
                  variant={adjustData.type === 'deduct' ? 'danger' : 'outline-danger'}
                  onClick={() => setAdjustData({...adjustData, type: 'deduct'})}
                  style={{ width: '60px', fontSize: '1.2rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  type="button"
                >
                  <FaMinus />
                </Button>
                <Button 
                  variant={adjustData.type === 'add' ? 'success' : 'outline-success'}
                  onClick={() => setAdjustData({...adjustData, type: 'add'})}
                  style={{ width: '60px', fontSize: '1.2rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  type="button"
                >
                  <FaPlus />
                </Button>
                <Form.Control 
                  type="number" 
                  step="0.01"
                  min="0.01"
                  required 
                  className="fw-bold text-center"
                  style={{ color: !adjustData.type ? '#495057' : (adjustData.type === 'deduct' ? '#dc3545' : '#198754') }}
                  value={adjustData.adjust_quantity} 
                  onChange={(e) => setAdjustData({...adjustData, adjust_quantity: e.target.value})} 
                  placeholder="e.g. 50" 
                />
              </InputGroup>
              <Form.Text className="text-muted d-block mt-2">
                Click <strong>- (Deduct)</strong> to record wastage/loss (deducts via FIFO cost).<br/>
                Click <strong>+ (Add)</strong> to record found stock (adds at 0 cost).
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold text-dark">Reason / Note</Form.Label>
              <Form.Control 
                as="textarea"
                rows={3}
                required 
                className="bg-light"
                value={adjustData.reason} 
                onChange={(e) => setAdjustData({...adjustData, reason: e.target.value})} 
                placeholder="e.g. Spilled, Found extra, Expired..." 
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0 pb-4 px-4 d-flex justify-content-between">
            <Button variant="light" className="px-4" onClick={() => setShowAdjustModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit" className="px-5 shadow-sm" style={{ background: "#1e3c72", border: "none" }}>Confirm Adjustment</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* View Lots Modal */}
      <Modal show={showLotsModal} onHide={() => setShowLotsModal(false)} centered size="lg">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title style={{ color: "#1e3c72", fontWeight: 700 }}>
            Remaining Lots (FIFO) - {selectedItemName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4 pb-4">
          {loadingLots ? (
            <div className="text-center py-4">Loading lots...</div>
          ) : lotsData.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <FaBoxOpen size={48} className="mb-3 opacity-25" />
              <h5>No active lots found</h5>
              <p>This item has 0 stock remaining in the FIFO queue.</p>
            </div>
          ) : (
            <Table responsive hover className="align-middle mt-3">
              <thead style={{ backgroundColor: "#f1f4f8" }}>
                <tr>
                  <th className="py-2 text-secondary border-0 rounded-start">Date (FIFO Order)</th>
                  <th className="py-2 text-secondary border-0">Source / Vendor</th>
                  <th className="py-2 text-secondary border-0 text-center">Remaining Qty</th>
                  <th className="py-2 text-secondary border-0 text-end rounded-end">Unit Cost</th>
                </tr>
              </thead>
              <tbody>
                {lotsData.map((lot, idx) => (
                  <tr key={lot.id}>
                    <td className="py-3 fw-semibold">
                      <span className="me-2 text-muted small">#{idx + 1}</span>
                      {new Date(lot.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3">
                      {lot.vendor === 'inventory_adjustments' ? (
                        <Badge bg="info">Adjustment (+)</Badge>
                      ) : (
                        <span className="text-dark">{lot.vendor || 'Unknown Invoice'}</span>
                      )}
                    </td>
                    <td className="py-3 text-center fw-bold text-success">
                      {Number(lot.remaining_quantity).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="py-3 text-end fw-bold">
                      ฿{Number(lot.unit_cost).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
}
